import { put, takeLatest, call, delay, select } from 'redux-saga/effects';
import CHATBOT_TYPES from './_actionTypes';
import api from '../../utils/API/api';
import API_ENDPOINTS from '../../utils/API/endpoints';

// Build the history array the backend expects from Redux messages.
// - Excludes the last message (the current user message being sent right now).
// - Converts our internal role 'bot' → 'model' as the API requires.
// - Only includes fully complete (non-streaming) messages.
function buildHistory(messages) {
  const complete = messages.filter((m) => !m.isStreaming);
  // Drop the last user message — that's the one we're sending as `message`
  const history = complete.slice(0, -1);
  return history.map((m) => ({
    role: m.role === 'bot' ? 'model' : 'user',
    text: m.text,
  }));
}

// Streams the reply text word-by-word to mimic a typing effect.
function* streamReply(text) {
  const words = text.split(' ');
  for (let i = 0; i < words.length; i++) {
    const token = i === 0 ? words[i] : ' ' + words[i];
    yield put({ type: CHATBOT_TYPES.APPEND_BOT_TOKEN, payload: { token } });
    yield delay(35 + Math.random() * 25);
  }
}

function* handleSendMessage(action) {
  try {
    const { message } = action.payload;

    // Grab current messages to build conversation history
    const messages = yield select((state) => state.chatbot.messages);
    const history = buildHistory(messages);

    // Show empty bot bubble + typing indicator
    yield put({ type: CHATBOT_TYPES.BOT_TYPING_START });

    const response = yield call([api, api.post], API_ENDPOINTS.CHATBOT.CHAT, {
      message,
      history,
    });

    const data = response?.data?.data;
    const reply = data?.reply;

    if (!reply) {
      throw new Error('Empty response from server');
    }

    // Store rate limit info from successful response
    if (data?.rate_limit) {
      yield put({
        type: CHATBOT_TYPES.UPDATE_RATE_LIMIT,
        payload: data.rate_limit,
      });
    }

    // Stream the reply word-by-word
    yield call(streamReply, reply);

    yield put({ type: CHATBOT_TYPES.BOT_TYPING_STOP });
    yield put({ type: CHATBOT_TYPES.SEND_MESSAGE_SUCCESS });
  } catch (error) {
    yield put({ type: CHATBOT_TYPES.BOT_TYPING_STOP });

    const status = error?.response?.status;
    const responseData = error?.response?.data;
    let errorMessage = 'Something went wrong. Please try again.';

    if (status === 429) {
      const message = responseData?.message || '';
      if (message === 'Chatbot is currently disabled') {
        yield put({ type: CHATBOT_TYPES.CHATBOT_DISABLED });
        return;
      }
      yield put({
        type: CHATBOT_TYPES.RATE_LIMIT_EXCEEDED,
        payload: {
          message,
          daily_limit: responseData?.errors?.daily_limit ?? 0,
        },
      });
      return;
    }

    if (status === 401) {
      errorMessage = 'Session expired. Please log in again.';
    } else if (status === 400) {
      errorMessage = responseData?.message || 'Invalid request.';
    } else if (status === 500) {
      errorMessage = 'Server error. Please try again in a moment.';
    }

    yield put({
      type: CHATBOT_TYPES.SEND_MESSAGE_FAILURE,
      payload: { error: errorMessage },
    });
  }
}

function* chatbotSaga() {
  yield takeLatest(CHATBOT_TYPES.SEND_MESSAGE_REQUEST, handleSendMessage);
}

export default chatbotSaga;
